import { Link, Navigate, useParams } from 'react-router-dom';
import { Seo, Mark, Container, StatBand, MediaSection } from '../components/marketing/Primitives';
import { getProduct } from '../data/products';

export default function ProductPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  if (!product) return <Navigate to="/" replace />;

  const related = (product.related || []).map(getProduct).filter(Boolean);

  return (
    <>
      <Seo title={product.name} description={`${product.headline} ${product.subhead}`} />

      {/* Product hero */}
      <section className="border-b border-hairline bg-surface">
        <Container className="py-20 md:py-24">
          <Mark>{product.audienceTag}</Mark>
          <h1 className="mt-4 max-w-[22ch] font-serif text-4xl leading-tight md:text-5xl">{product.headline}</h1>
          <p className="mt-5 max-w-measure-wide text-lg leading-relaxed text-slate">{product.subhead}</p>
          <div className="mt-9 flex flex-wrap gap-4">
            {product.cta.external ? (
              <a href={product.cta.to} className="btn-primary" target="_blank" rel="noreferrer">
                {product.cta.label}
              </a>
            ) : (
              <Link to={product.cta.to} className="btn-primary">
                {product.cta.label}
              </Link>
            )}
            <Link to="/contact" className="btn-secondary">
              Request a demo
            </Link>
          </div>
        </Container>
      </section>

      {/* Body + features */}
      <section className="py-20 md:py-24">
        <Container>
          <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:gap-20">
            <div className="space-y-5">
              {product.body.map((para) => (
                <p key={para.slice(0, 40)} className="max-w-measure-wide leading-relaxed text-slate">
                  {para}
                </p>
              ))}
              {product.scopeNote && (
                <aside className="max-w-measure-wide rounded border border-hairline bg-surface p-6">
                  <p className="text-sm font-semibold">A note on scope</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{product.scopeNote}</p>
                </aside>
              )}
            </div>
            <div className="space-y-8 border-hairline lg:border-l lg:pl-10">
              {product.features.map((f) => (
                <div key={f.title}>
                  <h2 className="flex items-start gap-2 font-serif text-lg">
                    <span aria-hidden="true" className="mt-2.5 inline-block h-2 w-2 shrink-0 rounded-[1px] bg-seal" />
                    {f.title}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Stats, when the product has verifiable numbers */}
      {product.stats && <StatBand stats={product.stats} />}

      {/* Demo film and screenshots */}
      <MediaSection media={product.media} name={product.name} />

      {/* Deep dive */}
      <section className="py-20 md:py-24">
        <Container>
          <Mark>In depth</Mark>
          <h2 className="mt-3 max-w-[26ch] font-serif text-3xl md:text-4xl">{product.deepDive.heading}</h2>
          <p className="mt-5 max-w-measure-wide leading-relaxed text-slate">{product.deepDive.intro}</p>
          <dl className="mt-12 grid gap-x-14 gap-y-10 md:grid-cols-2">
            {product.deepDive.items.map((item) => (
              <div key={item.title} className="border-t border-hairline pt-5">
                <dt className="font-serif text-lg">{item.title}</dt>
                <dd className="mt-2 leading-relaxed text-slate">{item.text}</dd>
              </div>
            ))}
          </dl>
        </Container>
      </section>

      {/* Worked example */}
      <section className="border-y border-hairline bg-surface py-20">
        <Container>
          <div className="grid gap-10 md:grid-cols-[1fr_1.6fr] md:gap-16">
            <div>
              <Mark>An example</Mark>
              <h2 className="mt-3 font-serif text-3xl">{product.exampleHeading}</h2>
            </div>
            <p className="leading-relaxed text-slate">{product.example}</p>
          </div>
        </Container>
      </section>

      {/* CTA + related */}
      <section className="py-20">
        <Container>
          <div className="flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="max-w-[24ch] font-serif text-3xl">Ready to look closer?</h2>
              <div className="mt-6 flex flex-wrap gap-4">
                {product.cta.external ? (
                  <a href={product.cta.to} className="btn-primary" target="_blank" rel="noreferrer">
                    {product.cta.label}
                  </a>
                ) : (
                  <Link to={product.cta.to} className="btn-primary">
                    {product.cta.label}
                  </Link>
                )}
              </div>
            </div>
            {related.length > 0 && (
              <div>
                <p className="text-sm text-slate">Related products</p>
                <ul className="mt-2 space-y-1">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/products/${r.slug}`} className="link-quiet font-serif">
                        {r.name}
                      </Link>
                      <span className="ml-2 text-sm text-slate">{r.audienceTag}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Container>
      </section>
    </>
  );
}
